import { useQuery } from "@tanstack/react-query";
import { companyApi, companyQueryKeys } from "service/crm/api/company";

interface UseQueryOptions {
  enabled?: boolean;
}

// Step 1 alone: companyKey -> uuid
export const useResolveCompanyUuid = (
  companyKey: string,
  options: UseQueryOptions = {},
) => {
  return useQuery({
    queryKey: companyQueryKeys.resolve(companyKey),
    queryFn: () => companyApi.resolveCompanyUuidByKey(companyKey),
    enabled: (options.enabled ?? true) && !!companyKey,
  });
};

// Step 2 alone: uuid -> full company detail
export const useCompanyDetails = (
  companyUuid: string,
  options: UseQueryOptions = {},
) => {
  return useQuery({
    queryKey: companyQueryKeys.detail(companyUuid),
    queryFn: () => companyApi.getCompany(companyUuid),
    enabled: (options.enabled ?? true) && !!companyUuid,
  });
};

// Composed: companyKey -> uuid -> full company detail
// This is what the detail page should use.
export const useCompanyDetailsByKey = (companyKey: string) => {
  const {
    data: uuid,
    isLoading: isResolving,
    isError: isResolveError,
  } = useResolveCompanyUuid(companyKey);

  const {
    data: company,
    isLoading: isLoadingDetail,
    isError: isDetailError,
  } = useCompanyDetails(uuid ?? "", { enabled: !!uuid });

  return {
    company,
    isLoading: isResolving || isLoadingDetail,
    isError: isResolveError || isDetailError,
  };
};